import { useEffect } from "react";
import { SITE, ORG_LD, WEBSITE_LD } from "@/lib/seo";

/**
 * Lightweight SEO + Schema.org component. No deps.
 * Sets <title>, <meta description>, canonical, OG/Twitter tags, and injects JSON-LD blocks.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image,
  jsonLd = [],
  noindex = false,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE.shortName}` : `${SITE.name} — Get More Than One Option Before You Sell`;
    const desc = description || SITE.description;
    const url = SITE.url + path;
    const ogImage = image || SITE.logo;

    document.title = fullTitle;

    setMeta("description", desc);
    setMeta("robots", noindex ? "noindex,nofollow" : "index,follow");
    setLink("canonical", url);

    // Open Graph
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:url", url, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:site_name", SITE.shortName, "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", ogImage);

    // JSON-LD: always include Organization + WebSite + page-specific blocks
    const blocks = [ORG_LD, WEBSITE_LD, ...(jsonLd || [])];
    const previous = Array.from(document.querySelectorAll('script[data-seo-jsonld="true"]'));
    previous.forEach((el) => el.remove());
    blocks.forEach((b) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seoJsonld = "true";
      s.text = JSON.stringify(b);
      document.head.appendChild(s);
    });

    return () => {
      // leave the head clean enough on unmount; new page will overwrite
    };
  }, [title, description, path, image, noindex, JSON.stringify(jsonLd)]);

  return null;
}

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
